import React, {useCallback, useContext, useEffect, useReducer, useRef, useState} from 'react';
import { handleTradingsymbolClick } from '../../../services/apiService';
import PriceDifferenceDisplay from "./PriceDifferenceDisplay";
import LastTradedPriceDisplay from "./LastTradedPriceDisplay";
import OverAllGainDisplay from "./OverAllGainDisplay";
import CurrentValueDisplay from "./CurrentValueDisplay";
import { DelayedTickProvider } from "../../context/DelayedTickContext";
import ActionButtonsWrapper from "@portfolio/gridcomponents/components/ActionButtons";
import { DataContext } from "../../../contexts/DataContext";
import { useAnnouncementContext } from "@dashboard/contexts/ExchangeAnnContext";
import { useInstrumentRowContext } from '../../../contexts/InstrumentRowContext';
import '../css/GUIInstrumentRow.css';
import {useRowSelectionContext} from "@portfolio/context/RowSelectionContextProvider";
import {flushSync} from "react-dom";
import { memo } from 'react';
import { useSyncExternalStore } from 'react';
import {formatIndianValue} from "@portfolio/utils/formatNumber";
import {useSharingContext} from "@dashboard/contexts/SharingContext";

const StaticTradingSymbolCell = ({ tradingsymbol, priceColor, hasNewAnnouncements, onClick }) => (
    <td className="pf-trading-token" style={{ color: priceColor }} onClick={onClick}>
        {tradingsymbol} <br />
        {hasNewAnnouncements && <button className="pf-announcement-button">A</button>}
    </td>
);

const StaticQuantityCell = React.memo(({ netQuantity, weightedAvgPrice }) => (
    <td className="net-quantity">
        <div  className={"quantity-pf"}>{netQuantity} </div>
        <span className="weighted-average">{weightedAvgPrice.toFixed(2)}</span>
    </td>
));

/*const StaticAmountInvestedCell = React.memo(({ amount }) => (
    <td>{amount.toFixed(2)}</td>
));*/

const PortfolioRow = ({
                          shouldUseStockForDisplay,
                          onDoubleClick,
                          rowWrapperRef,
                          index,
                          rowKey,
                      }) => {
    const [priceColor, setPriceColor] = useState('black');
    const [overAllGainColor, setOverAllGainColor] = useState('black');
    const [lastPriceColor, setLastPriceColor] = useState('black');

    const { screenerURLs } = useContext(DataContext);
    const { newAnnouncements } = useAnnouncementContext();
    const hasNewAnnouncements = newAnnouncements && newAnnouncements.length > 0;

    const { stock } = useInstrumentRowContext();

    // Expose a stable boolean for PortfolioGrid filters (Announcement-only mode).
    // This mirrors exactly the same condition that shows the "A" badge.
    useEffect(() => {
        if (stock?.instrumentEntity) {
            stock.instrumentEntity.__pfHasNewAnnouncements = !!hasNewAnnouncements;
        }
    }, [hasNewAnnouncements, stock]);
    const { updateSelectedRowIndex } = useRowSelectionContext();
    const { selectedRowKeyRef, rowRerenderTriggers } = useRowSelectionContext();
    const [isSelected, setIsSelected] = useState(() => rowKey === selectedRowKeyRef.current);
    const rowRef = useRef(null);
    const { showFormattedNumbers } = useSharingContext();

    useEffect(() => {
        rowRerenderTriggers.current.set(rowKey, () => {
            const selected = rowKey === selectedRowKeyRef.current;
            setIsSelected(selected);
        });

        return () => {
            rowRerenderTriggers.current.delete(rowKey);
        };
    }, [rowKey]);

    const StaticAmountInvestedCell = React.memo(({ amount }) => (
        <td><div><span>{formatIndianValue(amount, showFormattedNumbers)}</span></div></td>
    ));
    return (
        <DelayedTickProvider instrumentToken={stock.instrumentEntity.instrumentToken}>
            <tr
                key={rowKey} // ✅ Add this line to force rerender
                onClick={() => {
                    flushSync(() => {
                        updateSelectedRowIndex(rowKey, index);
                    });
                }}
                onDoubleClick={onDoubleClick}
                ref={rowWrapperRef}
                className={`pf-tr-item ${isSelected ? "pf-selected-row" : ""}`}
            >
                <StaticTradingSymbolCell
                    tradingsymbol={stock.instrumentEntity.tradingsymbol}
                    priceColor={priceColor}
                    hasNewAnnouncements={hasNewAnnouncements}
                    onClick={() => handleTradingsymbolClick(stock.instrumentEntity.tradingsymbol,screenerURLs)}
                />
                <td>
                    <PriceDifferenceDisplay
                        instrumentToken={stock.instrumentEntity.instrumentToken}
                        stock={stock}
                        priceColor={lastPriceColor}
                        setPriceColor={setPriceColor}
                        setLastPriceColor={setLastPriceColor}
                        /*onPriceDifferenceChange={handlePriceDifference}*/
                        contextName={'DelayedTickContext'}
                        shouldUseStockForDisplay={shouldUseStockForDisplay}
                    />
                </td>
                <td className="overall_gain_class">
                    <OverAllGainDisplay
                        instrumentToken={stock.instrumentEntity.instrumentToken}
                        stock={stock}
                        overAllGainColor={overAllGainColor}
                        setOverAllGainColor={setOverAllGainColor}
                        contextName={'DelayedTickContext'}
                        shouldUseStockForDisplay={shouldUseStockForDisplay}
                    />
                </td>
                <td className="actions-cell">
                    <ActionButtonsWrapper/>
                </td>
                <td className="current_value_class">
                    <CurrentValueDisplay
                        instrumentToken={stock.instrumentEntity.instrumentToken}
                        stock={stock}
                        contextName={'DelayedTickContext'}
                        shouldUseStockForDisplay={shouldUseStockForDisplay}
                    />
                </td>
                <StaticQuantityCell
                    netQuantity={stock.netQuantity}
                    weightedAvgPrice={stock.weightedAvgPrice}
                />
                <StaticAmountInvestedCell amount={stock.netQuantity * stock.weightedAvgPrice}/>
                <td>
                    <LastTradedPriceDisplay
                        instrumentToken={stock.instrumentEntity.instrumentToken}
                        priceColor={lastPriceColor}
                        contextName={'DelayedTickContext'}
                        stock={stock}
                        shouldUseStockForDisplay={shouldUseStockForDisplay}
                    />
                </td>
                <td></td>
            </tr>
        </DelayedTickProvider>
    );
};

export default memo(PortfolioRow);
